/**
 * custom-checkout.js — MenaHub Web Checkout
 * Handles multi-step form, validation, order creation via backend API
 *
 * cache-bust 2026-07-24-a — comment only, no logic change.
 * Shopify's CDN was serving a months-old build of this file (64,387 bytes)
 * while the published theme held the current one (95,473 bytes). The stale
 * build predates the quick-buy flow: it fetched /cart.js instead of
 * /cart.json and never called loadQuickProduct(), so ?quick=<handle>
 * checkouts built no synthetic cart, never ran updatePaylaterVisibility(),
 * and left the PayLater option display:none with an empty order summary —
 * i.e. PayLater checkout appeared broken for EVERY product. Editing the file
 * changes its bytes, which makes Shopify mint a new asset ?v= hash the CDN
 * has never cached. Bump this line again if the CDN ever pins an old copy.
 */
(function() {
  'use strict';

  /* Locale. Detected from the URL for the same reason mh-quickbuy.js does it
     this way: a Liquid-rendered locale can be baked into a cached page and then
     served to a shopper on the other language. */
  var IS_AR = /^\/ar(\/|$)/.test(location.pathname);
  var T = IS_AR ? {
    subtotal: 'المجموع الفرعي', delivery: 'التوصيل', free: 'مجاني', total: 'الإجمالي',
    freeOver: 'توصيل مجاني للطلبات فوق ',
    plPayments: ' أقساط', perMo: '/شهر',
    plSub: function (n, amt) { return n + ' × ' + amt + ' · بدون فوائد · بدون رسوم خفية'; },
    payFailed: 'فشل الدفع. يرجى المحاولة مرة أخرى.',
    cardError: 'خطأ في الدفع بالبطاقة. يرجى التحقق من بيانات بطاقتك والمحاولة مرة أخرى.',
    cardLoading: 'جارٍ تحميل نموذج البطاقة الآمن...',
    cardLoadFail: 'تعذّر تحميل نموذج البطاقة. لا يزال بإمكانك الدفع بعد إتمام الطلب.',
    cardInitFail: 'فشل تهيئة نموذج البطاقة.',
    netError: 'خطأ في الشبكة. يرجى التحقق من اتصالك والمحاولة مرة أخرى.',
    netErrorShort: 'خطأ في الشبكة. يرجى المحاولة مرة أخرى.',
    verifyPhoneFirst: 'يرجى التحقق من رقم هاتفك أولاً',
    addPhonePrompt: 'لا يوجد رقم هاتف مسجّل في حسابك. يرجى إدخال رقم هاتفك والتحقق منه للمتابعة.',
    addEmailPrompt: 'لا يوجد بريد إلكتروني مسجّل في حسابك. يرجى إدخال بريدك الإلكتروني للمتابعة.',
    emailRequired: 'يرجى إدخال بريد إلكتروني صحيح',
    disposableEmail: 'لا يُسمح بعناوين البريد المؤقتة. يرجى استخدام بريد حقيقي.',
    countryCode: 'يرجى اختيار رمز دولة صحيح',
    otpEmailTitle: 'تحقق من بريدك الإلكتروني',
    otpEmailLead: 'سنرسل رمز تحقق مكوّن من ٦ أرقام إلى بريدك الإلكتروني.',
    otpEmailEnter: 'أدخل الرمز المرسل إلى بريدك الإلكتروني',
    otpEmailBtn: 'تحقق من البريد ومتابعة',
    otpPhoneTitle: 'تحقق من رقم هاتفك',
    otpPhoneLead: 'سنرسل رمز تحقق مكوّن من ٦ أرقام إلى رقم هاتفك.',
    otpPhoneEnter: 'أدخل الرمز المرسل إلى هاتفك',
    otpPhoneBtn: 'تحقق ومتابعة',
    sending: 'جارٍ الإرسال...', sendOtp: 'إرسال رمز التحقق', otpFail: 'تعذّر إرسال الرمز',
    verifying: 'جارٍ التحقق...', verify: 'تحقق', otpInvalid: 'رمز التحقق غير صحيح',
    codeExpires: 'ينتهي الرمز خلال ', codeExpired: 'انتهت صلاحية الرمز',
    geoUnsupported: 'خدمة تحديد الموقع غير مدعومة في متصفحك.',
    geoFail: 'تعذّر تحديد موقعك. يرجى السماح بالوصول إلى الموقع أو تحديده يدوياً.',
    geoHint: 'حرّك المؤشر أو ابحث لتحديد الموقع', addNew: 'إضافة جديد'
  } : {
    subtotal: 'Subtotal', delivery: 'Delivery', free: 'FREE', total: 'Total',
    freeOver: 'Free delivery on orders ',
    plPayments: ' payments', perMo: '/mo',
    plSub: function (n, amt) { return n + ' \u00d7 ' + amt + ' \u00b7 0% interest \u00b7 no hidden fees'; },
    /* These were written as `T.payFailed`, `T.cardError`, … — a self-reference
       to the very object being assigned. `T` is still undefined while this
       initialiser runs, so the ENGLISH branch threw
       "TypeError: Cannot read properties of undefined (reading 'payFailed')"
       on every non-Arabic page load. That killed the whole IIFE before init(),
       so the checkout never booted: no cart was loaded, ?quick= never fetched
       the product, updatePaylaterVisibility() never ran, and PayLater stayed
       display:none with "Order Summary (0 items)" — for every product.
       Arabic was unaffected because IS_AR takes the other branch, which uses
       literals. Introduced 2026-07-23 by the Arabic translation pass. */
    payFailed: 'Payment failed. Please try again.',
    cardError: 'Card payment error. Please check your card details and try again.',
    cardLoading: 'Loading secure card form...',
    cardLoadFail: 'Could not load the card form. You can still pay after placing the order.',
    cardInitFail: 'Failed to initialise the card form.',
    netError: 'Network error. Please check your connection and try again.',
    netErrorShort: 'Network error. Please try again.',
    verifyPhoneFirst: 'Please verify your phone number first',
    addPhonePrompt: 'Your account has no phone number. Please enter and verify your phone number to continue.',
    addEmailPrompt: 'Your account has no email address. Please enter your email to continue.',
    emailRequired: 'Please enter a valid email address',
    disposableEmail: 'Temporary email addresses are not allowed. Please use a real one.',
    countryCode: 'Please select a valid country code',
    otpEmailTitle: 'Verify your email',
    otpEmailLead: "We'll send a 6-digit verification code to your email.",
    otpEmailEnter: 'Enter the code sent to your email',
    otpEmailBtn: 'Verify email and continue',
    otpPhoneTitle: 'Verify your phone number',
    otpPhoneLead: "We'll send a 6-digit verification code to your phone number.",
    otpPhoneEnter: 'Enter the code sent to your phone',
    otpPhoneBtn: 'Verify and continue',
    sending: 'Sending...', sendOtp: 'Send verification code', otpFail: 'Could not send the code',
    verifying: 'Verifying...', verify: 'Verify', otpInvalid: 'Invalid verification code',
    codeExpires: 'Code expires in ', codeExpired: 'Code expired',
    geoUnsupported: 'Location services are not supported in your browser.',
    geoFail: 'Could not determine your location. Please allow location access or set it manually.',
    geoHint: 'Move the pin or search to set the location', addNew: 'Add new'
  };


  // ── Config ──────────────────────────────────────────────────
  var BACKEND_URL = '';  // set from data attribute
  var CURRENCY = 'QAR';

  // ── Tracking helpers ─────────────────────────────────────────
  // Shopify's native checkout fires ecommerce events automatically;
  // a custom checkout has to fire them explicitly. We push to GTM's
  // dataLayer (which routes to GA4 + any other tags configured in
  // the GTM container) and to fbq directly for Meta Pixel. Every
  // call is wrapped in try/catch so a missing dataLayer or fbq
  // never breaks checkout. Idempotent per event name within a
  // session — guards against double-firing on re-renders.
  var _firedEvents = {};
  function _gtmPush(eventName, payload) {
    try {
      window.dataLayer = window.dataLayer || [];
      // Clear the previous ecommerce object before pushing a new
      // one — GTM's GA4 ecommerce tag treats this as the canonical
      // pattern (recommended in Google's docs).
      window.dataLayer.push({ ecommerce: null });
      var data = Object.assign({ event: eventName }, payload || {});
      window.dataLayer.push(data);
    } catch (e) {}
  }
  function _fbqTrack(eventName, payload) {
    try {
      if (typeof window.fbq === 'function') {
        window.fbq('track', eventName, payload || {});
      }
    } catch (e) {}
  }
  function _cartToGa4Items(cart) {
    return ((cart && cart.items) || []).map(function(it, idx) {
      return {
        item_id:      String(it.product_id || it.id || ''),
        item_name:    String(it.product_title || it.title || ''),
        item_brand:   String(it.vendor || ''),
        item_variant: String(it.variant_title || ''),
        price:        ((it.final_price != null ? it.final_price : it.price) || 0) / 100,
        quantity:     parseInt(it.quantity, 10) || 1,
        index:        idx,
      };
    });
  }
  function trackBeginCheckout(cart) {
    if (!cart || _firedEvents.begin_checkout) return;
    _firedEvents.begin_checkout = true;
    var items = _cartToGa4Items(cart);
    var value = (cart.total_price || 0) / 100;
    _gtmPush('begin_checkout', {
      ecommerce: { currency: CURRENCY, value: value, items: items }
    });
    _fbqTrack('InitiateCheckout', {
      content_ids: items.map(function(i){ return i.item_id; }),
      contents:    items.map(function(i){ return { id: i.item_id, quantity: i.quantity }; }),
      content_type: 'product',
      currency: CURRENCY,
      value: value,
      num_items: items.reduce(function(s, i){ return s + i.quantity; }, 0),
    });
  }
  function trackAddShippingInfo(cart) {
    if (!cart || _firedEvents.add_shipping_info) return;
    _firedEvents.add_shipping_info = true;
    var items = _cartToGa4Items(cart);
    var value = (cart.total_price || 0) / 100;
    _gtmPush('add_shipping_info', {
      ecommerce: {
        currency: CURRENCY,
        value: value,
        shipping_tier: 'Standard',
        items: items,
      }
    });
  }
  function trackAddPaymentInfo(cart, paymentMethod) {
    if (!cart) return;
    // Allow re-firing if the user changes payment method.
    var key = 'add_payment_info_' + paymentMethod;
    if (_firedEvents[key]) return;
    _firedEvents[key] = true;
    var items = _cartToGa4Items(cart);
    var value = (cart.total_price || 0) / 100;
    _gtmPush('add_payment_info', {
      ecommerce: {
        currency: CURRENCY,
        value: value,
        payment_type: paymentMethod || 'unknown',
        items: items,
      }
    });
    _fbqTrack('AddPaymentInfo', {
      currency: CURRENCY,
      value: value,
      content_ids: items.map(function(i){ return i.item_id; }),
    });
  }
  function trackPurchase(orderNumber, totalQar, currency, cart, paymentMethod) {
    if (_firedEvents.purchase) return;
    _firedEvents.purchase = true;
    var items = _cartToGa4Items(cart);
    var value = parseFloat(totalQar) || 0;
    var cur = currency || CURRENCY;
    _gtmPush('purchase', {
      ecommerce: {
        transaction_id: String(orderNumber || ''),
        currency:       cur,
        value:          value,
        payment_type:   paymentMethod || '',
        items:          items,
      }
    });
    _fbqTrack('Purchase', {
      content_ids:  items.map(function(i){ return i.item_id; }),
      contents:     items.map(function(i){ return { id: i.item_id, quantity: i.quantity }; }),
      content_type: 'product',
      currency:     cur,
      value:        value,
      num_items:    items.reduce(function(s, i){ return s + i.quantity; }, 0),
    });
  }

  var DISPOSABLE_DOMAINS = [
    'yopmail.com','mailinator.com','guerrillamail.com','tempmail.com',
    'throwaway.email','temp-mail.org','dispostable.com','sharklasers.com',
    'guerrillamailblock.com','grr.la','guerrillamail.info','guerrillamail.net',
    'mailnesia.com','maildrop.cc','discard.email','trashmail.com',
    'fakeinbox.com','tempinbox.com','getnada.com','10minutemail.com',
    'mohmal.com','emailondeck.com','tempr.email','burnermail.io',
    'minutemail.com','mailcatch.com','yopmail.fr','yopmail.net',
    'cool.fr.nf','jetable.fr.nf','nospam.ze.tc','nomail.xl.cx',
    'mega.zik.dj','speed.1s.fr','courriel.fr.nf','moncourrier.fr.nf'
  ];

  var GCC_PHONE_RULES = {
    '+974': { digits: 8, country: 'Qatar' },
    '+971': { digits: 9, country: 'UAE' },
    '+966': { digits: 9, country: 'Saudi Arabia' },
    '+965': { digits: 8, country: 'Kuwait' },
    '+973': { digits: 8, country: 'Bahrain' },
    '+968': { digits: 8, country: 'Oman' }
  };

  // ── State ───────────────────────────────────────────────────
  var currentStep = 1;
  var cartData = null;
  var isQuickMode = false; // single-product Quick Buy — real cart untouched
  var phoneVerified = false;
  var sessionPrefilled = false; // true when Step 1 is auto-filled from a live session
  var otpToken = '';       // HMAC token from server
  var otpTs = 0;           // timestamp paired with token
  var otpIdentifier = '';  // phone or email the OTP was issued against
  var otpChannel = 'sms';  // 'sms' when SMSCountry is enabled, else 'email'
  var otpTimerInterval = null;
  var otpExpiresAt = 0;

  // Delivery fee config (fetched from backend)
  var deliveryConfig = { fee: 18, free_minimum: 300, free_delivery_override: false };
  var currentDeliveryFee = 0;

  // PayLater BNPL config (fetched from checkout_config)
  var paylaterConfig = { enabled: false, min_order: 300, max_order: 25000, installments: 4 };

  // ── DOM refs ────────────────────────────────────────────────
  var root, panels, steps, stepLines, summary, overlay, thanks, alertBox;

  // ── Init ────────────────────────────────────────────────────
  function init() {
    root = document.getElementById('mh-checkout');
    if (!root) return;

    BACKEND_URL = root.getAttribute('data-backend-url') || '';
    CURRENCY = root.getAttribute('data-currency') || 'QAR';

    panels = root.querySelectorAll('.mh-co-panel');
    steps = root.querySelectorAll('.mh-co-step');
    stepLines = root.querySelectorAll('.mh-co-step-line');
    summary = root.querySelector('.mh-co-summary');
    overlay = root.querySelector('.mh-co-overlay');
    thanks = root.querySelector('.mh-co-thanks');
    alertBox = root.querySelector('.mh-co-alert');

    // Bind buttons
    var btnNext1 = root.querySelector('[data-action="next-1"]');
    var btnNext2 = root.querySelector('[data-action="next-2"]');
    var btnBack1 = root.querySelector('[data-action="back-1"]');
    var btnBack2 = root.querySelector('[data-action="back-2"]');
    var btnPlace = root.querySelector('[data-action="place-order"]');

    if (btnNext1) btnNext1.addEventListener('click', function() { goToStep(2); });
    if (btnNext2) btnNext2.addEventListener('click', function() { goToStep(3); });
    if (btnBack1) btnBack1.addEventListener('click', function() { goToStep(1); });
    if (btnBack2) btnBack2.addEventListener('click', function() { goToStep(2); });
    if (btnPlace) btnPlace.addEventListener('click', placeOrder);

    // OTP popup buttons
    var btnSendOtp = root.querySelector('[data-action="send-otp"]');
    var btnDoSendOtp = root.querySelector('[data-action="do-send-otp"]');
    var btnVerifyOtp = root.querySelector('[data-action="verify-otp"]');
    var btnResendOtp = root.querySelector('[data-action="resend-otp"]');
    var btnCloseOtp = root.querySelectorAll('[data-action="close-otp"]');
    if (btnSendOtp) btnSendOtp.addEventListener('click', openOtpPopup);
    if (btnDoSendOtp) btnDoSendOtp.addEventListener('click', sendOtp);
    if (btnVerifyOtp) btnVerifyOtp.addEventListener('click', verifyOtp);
    if (btnResendOtp) btnResendOtp.addEventListener('click', sendOtp);
    btnCloseOtp.forEach(function(b) { b.addEventListener('click', closeOtpPopup); });

    // Complete-your-details popup (account missing phone/email)
    var btnCollectContinue = root.querySelector('[data-action="collect-continue"]');
    var btnCloseCollect = root.querySelectorAll('[data-action="close-collect"]');
    if (btnCollectContinue) btnCollectContinue.addEventListener('click', collectContinue);
    btnCloseCollect.forEach(function(b) { b.addEventListener('click', closeCollect); });

    // Payment method radios
    var pmOptions = root.querySelectorAll('.mh-co-pm-option:not(.disabled)');
    pmOptions.forEach(function(opt) {
      opt.addEventListener('click', function() {
        pmOptions.forEach(function(o) { o.classList.remove('selected'); });
        opt.classList.add('selected');
        var radio = opt.querySelector('input[type="radio"]');
        if (radio) radio.checked = true;
        // Show/hide inline card form
        var cardWrap = document.getElementById('mhTapCardWrap');
        if (cardWrap) {
          if (radio && radio.value === 'card') {
            cardWrap.style.display = '';
            initTapCardSDK();
          } else {
            cardWrap.style.display = 'none';
          }
        }
      });
    });

    // Clear field errors on input
    root.querySelectorAll('.mh-co-input').forEach(function(input) {
      input.addEventListener('input', function() {
        this.classList.remove('error');
        var errEl = this.parentElement.querySelector('.mh-co-error');
        if (errEl) errEl.classList.remove('show');
      });
    });

    // Always reset UI on page load — prevents stuck spinner + disabled button
    // when user navigates back from Tap/BNPL payment page via browser back
    showOverlay(false);
    var placeBtn = root.querySelector('[data-action="place-order"]');
    if (placeBtn) placeBtn.disabled = false;

    // Check for Tap payment result redirect
    var urlParams = new URLSearchParams(window.location.search);
    var tapStatus = urlParams.get('tap_status');
    if (tapStatus === 'success') {
      // Check if this was a split BNPL order — only remove eligible items
      var splitVariants = null;
      try { splitVariants = JSON.parse(sessionStorage.getItem('bnpl_split_variants') || 'null'); } catch(e) {}
      if (splitVariants && Array.isArray(splitVariants) && splitVariants.length > 0) {
        // Remove only BNPL items from cart, keep the rest
        sessionStorage.removeItem('bnpl_split_variants');
        var removePromises = splitVariants.map(function(variantId) {
          return fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: 0 })
          }).catch(function() {});
        });
        Promise.all(removePromises).then(function() {
          // Update cart badge with remaining items
          fetch('/cart.json').then(function(r) { return r.json(); }).then(function(c) {
            document.querySelectorAll('.bigcounter').forEach(function(el) {
              el.textContent = '' + (c.item_count || 0);
            });
          }).catch(function() {});
        });
      } else {
        clearCart();
      }
      showThankYou(
        urlParams.get('order') || '',
        parseFloat(urlParams.get('amount') || '0'),
        urlParams.get('currency') || 'QAR'
      );
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }
    if (tapStatus === 'failed') {
      showAlert(urlParams.get('message') || T.payFailed, 'error');
      window.history.replaceState({}, '', window.location.pathname);
    }

    // Saved address + map picker bindings
    var mapPickBtn   = document.getElementById('coMapPickBtn');
    var addNewBtn    = document.getElementById('coAddNewAddrBtn');
    var mapClose     = document.getElementById('coMapModalClose');
    var mapBack      = document.getElementById('coMapModalBack');
    var mapUseLoc    = document.getElementById('coMapUseLocation');
    var mapConfirm   = document.getElementById('coMapConfirmPin');
    if (mapPickBtn)  mapPickBtn.addEventListener('click', openMapPicker);
    if (addNewBtn)   addNewBtn.addEventListener('click', openMapPicker);
    if (mapClose)    mapClose.addEventListener('click', closeMapPicker);
    if (mapBack)     mapBack.addEventListener('click', closeMapPicker);
    if (mapUseLoc)   mapUseLoc.addEventListener('click', useCurrentLocation);
    if (mapConfirm)  mapConfirm.addEventListener('click', confirmMapPin);

    // Label chip selection
    document.querySelectorAll('.mh-map-label-chip').forEach(function(chip) {
      chip.addEventListener('click', function() {
        document.querySelectorAll('.mh-map-label-chip').forEach(function(c) { c.classList.remove('selected'); });
        chip.classList.add('selected');
      });
    });

    // Hide "Save to account" checkbox if not logged in
    var saveWrap = document.getElementById('coMapSaveToAccountWrap');
    var sessionToken = '';
    try { sessionToken = localStorage.getItem('mh_token') || ''; } catch(e) {}
    if (saveWrap && !sessionToken) saveWrap.style.display = 'none';

    loadCart();
    prefillFromSession();
    loadSavedAddresses();
  }

  // ── Saved Addresses ─────────────────────────────────────────

  var savedAddressCount = 0; // how many addresses are on the account (0 = none saved)

  function loadSavedAddresses() {
    var token = '';
    try { token = localStorage.getItem('mh_token') || ''; } catch(e) {}
    if (!token) return; // guest — no saved addresses

    fetch(BACKEND_URL + 'addresses.php?action=list', {
      headers: { 'Authorization': 'Bearer ' + token }
    })
    .then(function(r) { return r.json(); })
    .then(function(data) {
      if (!data.success || !Array.isArray(data.data)) return;
      savedAddressCount = data.data.length;
      if (savedAddressCount === 0) return;
      renderSavedAddresses(data.data);
    })
    .catch(function() { /* non-fatal */ });
  }

  // Save the typed shipping address onto the logged-in account when nothing is
  // saved yet, so next time it prefills and the user doesn't retype it. Also
  // re-persists the contact phone/email as a safety net. Fire-and-forget — the
  // order proceeds regardless. Called from placeOrder() after validation.
  function persistCheckoutDetails() {
    var token = '';
    try { token = localStorage.getItem('mh_token') || ''; } catch(e) {}
    if (!token) return; // guest checkout — nothing to persist

    var val = function(sel) { var el = root.querySelector(sel); return el ? (el.value || '').trim() : ''; };

    // Keep the account's phone/email current (covers "save the phone too").
    persistProfileContact(
      val('[name="co_phone_code"]') || '+974',
      val('[name="co_phone_number"]'),
      val('[name="co_email"]').toLowerCase()
    );

    // Only create an address when the account has none saved.
    if (savedAddressCount > 0) return;

    var phoneDigits = val('[name="co_phone_number"]').replace(/\D/g, '');
    var payload = {
      action: 'save',
      city: val('[name="co_city"]'),
      area: val('[name="co_area"]'),
      street: val('[name="co_street"]'),
      building: val('[name="co_building"]'),
      zip: val('[name="co_zip"]'),
      notes: val('[name="co_notes"]'),
      phone: phoneDigits ? ((val('[name="co_phone_code"]') || '+974') + phoneDigits) : '',
      label: 'home',
      is_default: 1
    };
    // Need at least the core address parts to bother saving.
    if (!payload.city || (!payload.building && !payload.street)) return;

    fetch(BACKEND_URL + 'addresses.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
      body: JSON.stringify(payload)
    })
    .then(function(r) { return r.json(); })
    .then(function(d) { if (d && d.success) savedAddressCount = 1; })
    .catch(function() { /* non-fatal — order still proceeds */ });
  }

  // Detect Google Plus Codes (e.g. "6GQR+4WJ") — garbage from bad reverse-geocoding
  // NOT anchored: catches "6GQR+4WJ, 6GQR+4WJ" and "Villa 22, 6GQR+4WJ" too
  function isPlusCode(v) { return /[A-Z0-9]{4}\+[A-Z0-9]{2,}/i.test((v || '').trim()); }
  function isNumericOnly(v) { return /^\d+$/.test((v || '').trim()); }
  // Detect area/street/building values that are really just the city name (bad data)
  var QA_CITIES = ['doha','al wakrah','al khor','al rayyan','umm salal','al daayen','al shamal','lusail','mesaieed','dukhan','qatar'];
  function isCityName(v) { return QA_CITIES.indexOf((v || '').toLowerCase().trim()) !== -1; }
  function isGarbage(v) { return isPlusCode(v) || isCityName(v); }

  // Label → icon map
  var LABEL_ICONS = {
    'home'  : '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>',
    'work'  : '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>',
    'other' : '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>'
  };
  var PIN_ICON = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>';

  function renderSavedAddresses(addresses) {
    var section  = document.getElementById('coSavedAddrSection');
    var scroll   = document.getElementById('coAddrScroll');
    if (!section || !scroll) return;

    scroll.innerHTML = '';

    addresses.forEach(function(addr) {
      var card = document.createElement('div');
      card.className = 'mh-addr-card';
      card.setAttribute('data-addr-id', addr.id);

      var rawLabel = (addr.label || 'address').toLowerCase();
      var iconSvg  = LABEL_ICONS[rawLabel] || PIN_ICON;
      var dispLabel = addr.label ? (addr.label.charAt(0).toUpperCase() + addr.label.slice(1)) : 'Address';

      var cityStr = addr.city || '';
      var areaStr = (!isGarbage(addr.area)     && addr.area)     ? addr.area     : '';
      var stStr   = (!isGarbage(addr.street)   && addr.street)   ? addr.street   : '';
      var bldgStr = (!isGarbage(addr.building) && addr.building) ? addr.building : '';

      // Line 1: City + Zone
      var line1txt = cityStr;
      if (areaStr) line1txt += ', Zone ' + areaStr;

      // Line 2: Street + Building (with readable prefixes if numeric)
      var parts2 = [];
      if (stStr)   parts2.push(isNumericOnly(stStr)   ? 'St '   + stStr   : stStr);
      if (bldgStr) parts2.push(isNumericOnly(bldgStr) ? 'Bldg ' + bldgStr : bldgStr);
      var line2txt = parts2.join(', ');

      card.innerHTML =
        '<div class="mh-addr-card-top">' +
          '<div class="mh-addr-card-icon">' + iconSvg + '</div>' +
          '<div class="mh-addr-card-label">' + dispLabel + '</div>' +
        '</div>' +
        '<div class="mh-addr-card-line">' + (line1txt || '—') + '</div>' +
        (line2txt ? '<div class="mh-addr-card-line">' + line2txt + '</div>' : '') +
        '<div class="mh-addr-card-check"><svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3.5"><polyline points="20 6 9 17 4 12"/></svg></div>';

      card.addEventListener('click', function() {
        scroll.querySelectorAll('.mh-addr-card').forEach(function(c) { c.classList.remove('selected'); });
        card.classList.add('selected');
        fillFromAddress(addr);
      });

      scroll.appendChild(card);

      // Auto-select default address
      if (addr.is_default == 1 || addr.is_default === true) {
        card.classList.add('selected');
        fillFromAddress(addr);
      }
    });

    // "Add new" card
    var newCard = document.createElement('div');
    newCard.className = 'mh-addr-card-new';
    newCard.innerHTML =
      '<div class="mh-addr-card-new-icon">' +
        '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>' +
      '</div>' +
      '<span>' + T.addNew + '</span>';
    newCard.addEventListener('click', openMapPicker);
    scroll.appendChild(newCard);

    section.style.display = '';
  }

  function fillFromAddress(addr) {
    var cityEl     = root.querySelector('[name="co_city"]');
    var areaEl     = root.querySelector('[name="co_area"]');
    var streetEl   = root.querySelector('[name="co_street"]');
    var buildingEl = root.querySelector('[name="co_building"]');
    var zipEl      = root.querySelector('[name="co_zip"]');
    var notesEl    = root.querySelector('[name="co_notes"]');

    if (cityEl && addr.city) {
      var opts = cityEl.querySelectorAll('option');
      var matched = false;
      opts.forEach(function(o) {
        if (o.value.toLowerCase() === (addr.city || '').toLowerCase()) {
          cityEl.value = o.value;
          matched = true;
        }
      });
      if (!matched) cityEl.value = addr.city;
    }
    // Skip garbage (Plus Codes or city names stored in zone/street/building)
    if (areaEl)     areaEl.value     = isGarbage(addr.area)     ? '' : (addr.area      || '');
    if (streetEl)   streetEl.value   = isGarbage(addr.street)   ? '' : (addr.street    || '');
    if (buildingEl) buildingEl.value = isGarbage(addr.building) ? '' : (addr.building  || '');
    if (zipEl)      zipEl.value      = addr.postal_code || '';
    if (notesEl && addr.notes) notesEl.value = addr.notes;

    // Clear any validation errors
    [cityEl, areaEl, streetEl, buildingEl].forEach(function(el) {
      if (el) {
        el.classList.remove('error');
        var errEl = el.parentElement && el.parentElement.querySelector('.mh-co-error');
        if (errEl) errEl.classList.remove('show');
      }
    });
  }

  // ── Map Picker ───────────────────────────────────────────────

  // ── Map Picker state ─────────────────────────────────────────
  var _map = null;
  var _mapMarker = null;
  var _mapAutocomplete = null;
  var _mapLat = 25.2854;  // Default: Doha center
  var _mapLng = 51.5310;
  var _geocodeTimer = null;

  // Geocoded address components (updated by auto-geocode)
  var _geoCity = 'Doha', _geoZone = '', _geoStreet = '', _geoBuilding = '';
  var _geoFormatted = '';

  function openMapPicker() {
    var overlay = document.getElementById('coMapModalOverlay');
    if (!overlay) return;
    overlay.classList.add('open');
    document.body.style.overflow = 'hidden';
    // Reset preview state
    var optRow = document.getElementById('coMapOptionsRow');
    if (optRow) optRow.style.display = 'none';
    var prevText = document.getElementById('coMapAddrText');
    if (prevText) prevText.textContent = T.geoHint;
    loadMapsKeyAndInit();
    // Robust first-open fix: the map can be created while the modal is still
    // animating in (→ gray tiles). Retry resize+recenter a few times regardless
    // of init timing; each no-ops until _map exists, then paints the tiles.
    [300, 800, 1400].forEach(function (d) {
      setTimeout(function () {
        if (!_map || typeof google === 'undefined') return;
        google.maps.event.trigger(_map, 'resize');
        _map.setCenter({ lat: _mapLat, lng: _mapLng });
      }, d);
    });
  }

  function closeMapPicker() {
    var overlay = document.getElementById('coMapModalOverlay');
    if (overlay) overlay.classList.remove('open');
    document.body.style.overflow = '';
  }

  function loadMapsKeyAndInit() {
    if (_map) { google.maps.event.trigger(_map, 'resize'); return; }
    var token = '';
    try { token = localStorage.getItem('mh_token') || ''; } catch(e) {}

    var headers = token ? { 'Authorization': 'Bearer ' + token } : {};
    fetch(BACKEND_URL + 'get_maps_key.php', { headers: headers })
    .then(function(r) { return r.json(); })
    .then(function(d) {
      if (!d.success) return;
      var nonceB64 = d.nonce;
      var keyB64   = d.key;
      var nonceBytes = Uint8Array.from(atob(nonceB64), function(c) { return c.charCodeAt(0); });
      var keyBytes   = Uint8Array.from(atob(keyB64),   function(c) { return c.charCodeAt(0); });
      var decoded = new Uint8Array(keyBytes.length);
      for (var i = 0; i < keyBytes.length; i++) {
        decoded[i] = keyBytes[i] ^ nonceBytes[i % nonceBytes.length];
      }
      injectMapsScript(new TextDecoder().decode(decoded));
    })
    .catch(function() {});
  }

  function injectMapsScript(apiKey) {
    if (document.getElementById('coMapsScript')) return;
    window._coMapsReady = function() { initMap(); };
    var s = document.createElement('script');
    s.id  = 'coMapsScript';
    s.src = 'https://maps.googleapis.com/maps/api/js?key=' + apiKey
           + '&libraries=places&callback=_coMapsReady';
    s.async = true;
    document.head.appendChild(s);
  }

  function initMap() {
    var container = document.getElementById('coMapContainer');
    if (!container || _map) return;

    _map = new google.maps.Map(container, {
      center: { lat: _mapLat, lng: _mapLng },
      zoom: 17,
      disableDefaultUI: true,
      zoomControl: true,
      gestureHandling: 'greedy',
      restriction: {
        latLngBounds: { north: 26.2, south: 24.5, west: 50.5, east: 52.4 },
        strictBounds: false
      }
    });

    // First-open fix: the map is created while the modal is still animating in,
    // so its tiles can paint gray until a resize. Force a resize + recenter once
    // the modal has settled (and again on the next frame for safety).
    [200, 500].forEach(function (d) {
      setTimeout(function () {
        if (!_map) return;
        google.maps.event.trigger(_map, 'resize');
        _map.setCenter({ lat: _mapLat, lng: _mapLng });
      }, d);
    });

    // Single draggable marker
    _mapMarker = new google.maps.Marker({
      position: { lat: _mapLat, lng: _mapLng },
      map: _map,
      draggable: true
    });

    // On marker drag → auto reverse geocode
    _mapMarker.addListener('dragend', function() {
      var pos = _mapMarker.getPosition();
      _mapLat = pos.lat();
      _mapLng = pos.lng();
      debouncedReverseGeocode();
    });

    // On map click → move marker + auto reverse geocode
    _map.addListener('click', function(e) {
      _mapLat = e.latLng.lat();
      _mapLng = e.latLng.lng();
      _mapMarker.setPosition(e.latLng);
      debouncedReverseGeocode();
    });

    // Places autocomplete restricted to Qatar
    var searchInput = document.getElementById('coMapSearchInput');
    if (searchInput) {
      _mapAutocomplete = new google.maps.places.Autocomplete(searchInput, {
        componentRestrictions: { country: 'qa' },
        fields: ['geometry', 'name', 'formatted_address', 'address_components']
      });
      _mapAutocomplete.addListener('place_changed', function() {
        var place = _mapAutocomplete.getPlace();
        if (!place.geometry) return;
        _mapLat = place.geometry.location.lat();
        _mapLng = place.geometry.location.lng();
        _map.panTo(place.geometry.location);
        _map.setZoom(17);
        _mapMarker.setPosition(place.geometry.location);
        // Use the place's own address components (no extra geocode call needed)
        parseGeocodeComponents(place.address_components || []);
        _geoFormatted = place.formatted_address || '';
        updateMapPreview();
      });
    }
  }

  // Debounced reverse geocode — waits 400ms after last pin move
  function debouncedReverseGeocode() {
    clearTimeout(_geocodeTimer);
    _geocodeTimer = setTimeout(reverseGeocodePin, 400);
  }

  function reverseGeocodePin() {
    if (typeof google === 'undefined') return;
    var geocoder = new google.maps.Geocoder();
    geocoder.geocode({ location: { lat: _mapLat, lng: _mapLng } }, function(results, status) {
      if (status !== 'OK' || !results || !results[0]) return;
      var r = results[0];
      _geoFormatted = r.formatted_address || '';
      parseGeocodeComponents(r.address_components || []);
      updateMapPreview();
    });
  }

  // Update the address preview text + show options row
  function updateMapPreview() {
    var previewText = document.getElementById('coMapAddrText');
    var optRow = document.getElementById('coMapOptionsRow');
    if (previewText) {
      // Show a clean summary: "Zone 15, St 200, Bldg 42, Doha" or fallback to formatted
      var parts = [];
      if (_geoZone)     parts.push('Zone ' + _geoZone);
      if (_geoStreet)   parts.push('St ' + _geoStreet);
      if (_geoBuilding) parts.push('Bldg ' + _geoBuilding);
      parts.push(_geoCity || 'Doha');
      previewText.textContent = parts.length > 1 ? parts.join(', ') : (_geoFormatted || 'Location selected');
    }
    if (optRow) optRow.style.display = '';
  }

  function useCurrentLocation() {
    var btn = document.getElementById('coMapUseLocation');
    if (!navigator.geolocation) { alert(T.geoUnsupported); return; }
    if (btn) btn.classList.add('loading');
    navigator.geolocation.getCurrentPosition(
      function(pos) {
        _mapLat = pos.coords.latitude;
        _mapLng = pos.coords.longitude;
        if (_map) {
          var latlng = new google.maps.LatLng(_mapLat, _mapLng);
          _map.panTo(latlng);
          _map.setZoom(17);
          _mapMarker.setPosition(latlng);
        }
        if (btn) btn.classList.remove('loading');
        reverseGeocodePin(); // auto geocode after locate
      },
      function() {
        if (btn) btn.classList.remove('loading');
        alert(T.geoFail);
      }
    );
  }

  // Parse Google address_components into Qatar-style fields
  function parseGeocodeComponents(components) {
    _geoZone = ''; _geoStreet = ''; _geoBuilding = ''; _geoCity = 'Doha';

    components.forEach(function(c) {
      var types = c.types;

      // Zone — appears in sublocality as "Zone 15" or neighborhood
      if (types.indexOf('sublocality_level_1') > -1 || types.indexOf('sublocality') > -1 || types.indexOf('neighborhood') > -1) {
        var m = c.long_name.match(/Zone\s*(\d+)/i);
        if (m) _geoZone = m[1];
        // Sometimes it's just a number (e.g. "15")
        else if (/^\d{1,3}$/.test(c.long_name.trim())) _geoZone = c.long_name.trim();
      }

      // Street — route with "Street 200" or just a number
      if (types.indexOf('route') > -1) {
        var sm = c.long_name.match(/Street\s*(\d+)/i) || c.long_name.match(/(\d+)\s*Street/i) || c.long_name.match(/^(\d+)$/);
        if (sm) _geoStreet = sm[1];
        else _geoStreet = c.long_name; // named street like "Al Corniche"
      }

      // Building number
      if (types.indexOf('street_number') > -1) _geoBuilding = c.long_name;
      if (types.indexOf('premise') > -1 && !_geoBuilding) _geoBuilding = c.long_name;

      // City
      if (types.indexOf('locality') > -1 || types.indexOf('administrative_area_level_2') > -1) {
        _geoCity = c.long_name;
      }
    });
  }

  // Confirm: fill checkout form + optionally save to account + close modal
  function confirmMapPin() {
    // Fill checkout form fields from geocoded data
    var cityEl     = root.querySelector('[name="co_city"]');
    var areaEl     = root.querySelector('[name="co_area"]');
    var streetEl   = root.querySelector('[name="co_street"]');
    var buildingEl = root.querySelector('[name="co_building"]');

    if (cityEl && _geoCity) {
      var opts = cityEl.querySelectorAll('option');
      var matched = false;
      opts.forEach(function(o) {
        if (_geoCity.toLowerCase().indexOf(o.value.toLowerCase()) > -1) {
          cityEl.value = o.value;
          matched = true;
        }
      });
      if (!matched) cityEl.value = _geoCity;
    }
    if (areaEl)     areaEl.value     = _geoZone;
    if (streetEl)   streetEl.value   = _geoStreet;
    if (buildingEl) buildingEl.value = _geoBuilding;

    // Clear validation errors on filled fields
    [cityEl, areaEl, streetEl, buildingEl].forEach(function(el) {
      if (el) {
        el.classList.remove('error');
        var errEl = el.parentElement && el.parentElement.querySelector('.mh-co-error');
        if (errEl) errEl.classList.remove('show');
      }
    });

    // Save to account if checkbox is checked
    var labelChip = document.querySelector('.mh-map-label-chip.selected');
    var label  = labelChip ? labelChip.getAttribute('data-label') : 'Home';
    var saveIt = document.getElementById('coMapSaveToAccount');
    var token  = '';
    try { token = localStorage.getItem('mh_token') || ''; } catch(e) {}
    if (saveIt && saveIt.checked && token) {
      var addrData = {
        label: label, city: _geoCity, area: _geoZone,
        street: _geoStreet, building: _geoBuilding,
        lat: _mapLat, lng: _mapLng, is_default: 0
      };
      fetch(BACKEND_URL + 'addresses.php', {
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' },
        body: JSON.stringify(addrData)
      })
      .then(function(r) { return r.json(); })
      .then(function(d) { if (d.success) loadSavedAddresses(); })
      .catch(function() {});
    }

    closeMapPicker();
  }

  // ── Session pre-fill ────────────────────────────────────────
  // Fetch the user's real profile from the backend DB using the stored token,
  // fill Step 1 fields, and jump straight to Step 2 (Shipping).
  function prefillFromSession() {
    var token;
    try { token = localStorage.getItem('mh_token'); } catch(e) { return; }
    if (!token) return;

    // Logged-in users already verified their phone at sign-in (login/register
    // requires OTP), and the backend re-validates this session token when the
    // order is created — so never re-prompt OTP for them. Mark verified as soon
    // as a session token is present; the profile fetch below then prefills
    // email/phone so they skip Step 1 entirely.
    phoneVerified = true;

    fetch(BACKEND_URL + '/customer_profile.php', {
      method: 'GET',
      headers: { 'Authorization': 'Bearer ' + token }
    })
    .then(function(r) { return r.json(); })
    .then(function(d) {
      // Token invalid/expired — fall back to OTP so the user isn't dead-ended.
      if (!d.success || !d.data) { phoneVerified = false; return; }
      var profile = d.data;

      // ── Fill email (skip synthetic placeholder emails) ──
      var emailInput = root.querySelector('[name="co_email"]');
      var realEmail = (profile.email || '').trim();
      if (emailInput && realEmail.indexOf('@') !== -1
          && realEmail.indexOf('noemail.local') === -1
          && realEmail.indexOf('_ph_') === -1) {
        emailInput.value = realEmail;
      }

      // ── Fill phone (parse country code + number) ──
      var rawPhone = (profile.phone || '').replace(/\s/g, '');
      var phoneCodeInput = root.querySelector('[name="co_phone_code"]');
      var phoneNumInput  = root.querySelector('[name="co_phone_number"]');

      if (rawPhone && phoneCodeInput && phoneNumInput) {
        var matched = false;
        var codes = Object.keys(GCC_PHONE_RULES);
        for (var i = 0; i < codes.length; i++) {
          var c = codes[i];
          var stripped = c.replace('+', '');
          if (rawPhone.indexOf('+' + stripped) === 0) {
            phoneCodeInput.value = c;
            phoneNumInput.value  = rawPhone.slice(c.length);
            matched = true; break;
          }
          if (rawPhone.indexOf(stripped) === 0) {
            phoneCodeInput.value = c;
            phoneNumInput.value  = rawPhone.slice(stripped.length);
            matched = true; break;
          }
        }
        if (!matched && /^\d{8}$/.test(rawPhone)) {
          phoneCodeInput.value = '+974';
          phoneNumInput.value  = rawPhone;
        }
      }

      sessionPrefilled = true;

      // Update token in localStorage with fresh profile data
      try {
        localStorage.setItem('mh_user', JSON.stringify({
          id: profile.id, name: profile.name,
          email: realEmail, phone: profile.phone || ''
        }));
      } catch(e) {}

      // ── Does the account actually carry the details checkout needs? ──
      // Some accounts have an email but no phone (or vice-versa). Before this
      // check we blindly marked them verified and jumped to Step 2 — they then
      // hit a generic "Validation failed" at Step 3 because the backend rejects
      // the empty phone/email. Instead, keep them on Step 1 to fill the missing
      // field (phone still goes through OTP) and then proceed.
      var hasRealEmail = (realEmail.indexOf('@') !== -1
          && realEmail.indexOf('noemail.local') === -1
          && realEmail.indexOf('_ph_') === -1);
      var hasPhone = !!(phoneNumInput && phoneNumInput.value
          && phoneNumInput.value.replace(/\D/g, '').length >= 8);

      if (!hasPhone || !hasRealEmail) {
        // Missing a required detail. Keep them on Step 1 (its inputs back the
        // OTP/order flow), show a clear alert, and pop the "complete your
        // details" modal with ONLY the missing field(s). The modal verifies via
        // OTP and saves the detail to the account before advancing to Step 2.
        phoneVerified = hasPhone; // phone (if present) was OTP-verified at sign-in
        goToStep(1);
        showAlert(!hasPhone ? T.addPhonePrompt : T.addEmailPrompt, 'error');
        openProfileCollect({ phone: !hasPhone, email: !hasRealEmail });
        return;
      }

      // ── Everything present — user passed OTP at sign-in — skip to Step 2 ──
      phoneVerified = true;
      goToStep(2);
    })
    .catch(function() {
      // Network error — restore OTP requirement so the user can still verify
      // manually and isn't dead-ended on Step 1.
      phoneVerified = false;
    });
  }

  // Persist a just-verified phone/email onto the logged-in customer account.
  // Fixes accounts that had only an email (or only a phone): once the missing
  // detail is entered + OTP-verified at checkout, we store it so they aren't
  // asked again. Guest checkout (no token) is a no-op.
  function persistProfileContact(phoneCode, phoneNum, email) {
    var token, user = {};
    try { token = localStorage.getItem('mh_token'); } catch(e) { return; }
    if (!token) return;
    try { user = JSON.parse(localStorage.getItem('mh_user') || '{}') || {}; } catch(e) { user = {}; }

    var name = (user && user.name) ? String(user.name).trim() : '';
    if (!name) name = (email && email.indexOf('@') !== -1) ? email.split('@')[0] : 'Customer';

    var phone = '';
    var digits = String(phoneNum || '').replace(/\D/g, '');
    if (digits) phone = (phoneCode || '+974') + digits;

    var body = { name: name };
    if (phone) body.phone = phone;
    if (email && email.indexOf('@') !== -1) body.email = email;
    if (!body.phone && !body.email) return;

    fetch(BACKEND_URL + 'customer_profile.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
      body: JSON.stringify(body)
    })
    .then(function(r) { return r.json(); })
    .then(function(d) {
      if (d && d.success) {
        try {
          if (phone) user.phone = phone;
          if (body.email) user.email = body.email;
          user.name = name;
          localStorage.setItem('mh_user', JSON.stringify(user));
        } catch(e) {}
      }
    })
    .catch(function() { /* non-fatal — the order still proceeds */ });
  }

  // ── Load Cart ───────────────────────────────────────────────
  var cartBnplEligible = false;
  var bnplEligibleProductIds = []; // product IDs that qualify for BNPL

  function loadCart() {
    // Quick Buy (single product, e.g. WhatsApp/1-Click PayLater): show ONLY
    // the clicked product — NOT the shopper's existing cart. A synthetic
    // single-item cart is built so the real cart is never touched or cleared.
    var qp = new URLSearchParams(window.location.search);
    var quick = qp.get('quick');
    if (quick) {
      isQuickMode = true;
      var qq = parseInt(qp.get('qty') || '1', 10); if (!(qq > 0)) qq = 1;
      loadQuickProduct(quick, qq, qp.get('v'));
      return;
    }

    // Step 1: fetch Shopify cart
    fetch('/cart.json').then(function(r) { return r.json(); })
    .then(function(cart) {
      if (!cart.items || cart.items.length === 0) {
        window.location.href = '/cart';
        return;
      }
      proceedWithCart(cart);
    })
    .catch(function() {
      window.location.href = '/cart';
    });
  }

  // Build a synthetic one-item cart from a product handle so quick-buy
  // checkout shows only that product. Leaves the real Shopify cart intact.
  function loadQuickProduct(handle, qty, variantId) {
    fetch('/products/' + encodeURIComponent(handle) + '.js')
      .then(function(r) { if (!r.ok) throw 0; return r.json(); })
      .then(function(p) {
        var vs = p.variants || [];
        // Prefer the exact variant the shopper selected on the PDP (?v=…),
        // else the first available, else the first.
        var v = (variantId && vs.filter(function(x) { return String(x.id) === String(variantId); })[0])
          || vs.filter(function(x) { return x.available !== false; })[0] || vs[0];
        if (!v) { window.location.href = '/products/' + handle; return; }
        var line = v.price * qty;
        var cart = {
          items: [{
            id: v.id, variant_id: v.id, product_id: p.id, key: String(v.id),
            quantity: qty, title: p.title, product_title: p.title,
            variant_title: (v.title && v.title !== 'Default Title') ? v.title : '',
            handle: p.handle, sku: v.sku || '',
            price: v.price, final_price: v.price, discounted_price: v.price,
            line_price: line, final_line_price: line, original_line_price: line,
            image: p.featured_image || (p.images && p.images[0]) || '',
            requires_shipping: v.requires_shipping !== false
          }],
          item_count: qty, total_price: line, original_total_price: line,
          items_subtotal_price: line, currency: 'QAR',
          attributes: { checkout_intent: 'paylater' }
        };
        proceedWithCart(cart);
      })
      .catch(function() { window.location.href = '/cart'; });
  }

  // Shared: fetch config, apply BNPL/delivery, render + start the flow.
  function proceedWithCart(cart) {
    var handles = [];
    for (var i = 0; i < cart.items.length; i++) {
      var h = cart.items[i].handle;
      if (h && handles.indexOf(h) === -1) handles.push(h);
    }
    var handlesParam = handles.length > 0 ? '?handles=' + encodeURIComponent(handles.join(',')) : '';

    return fetch(BACKEND_URL + 'checkout_config.php' + handlesParam)
      .then(function(r) { return r.json(); })
      .catch(function() { return null; })
      .then(function(config) {
        if (config && config.success && config.data && config.data.delivery) {
          deliveryConfig = config.data.delivery;
        }
        if (config && config.success && config.data && config.data.paylater) {
          paylaterConfig = config.data.paylater;
        }
        if (config && config.success && config.data && config.data.otp_channel) {
          otpChannel = config.data.otp_channel === 'email' ? 'email' : 'sms';
        }
        applyOtpChannelUi();

        cartBnplEligible = !!(config && config.success && config.data && config.data.cart_bnpl_eligible);
        var rawIds = (config && config.data && config.data.bnpl_eligible_product_ids) || [];
        bnplEligibleProductIds = rawIds.map(function(id) { return parseInt(id, 10); }).filter(function(id) { return id > 0; });
        updatePaylaterVisibility(cart);

        cartData = cart;
        renderSummary(cart);
        trackBeginCheckout(cart);

        var hasSession = false;
        try { hasSession = !!localStorage.getItem('mh_token'); } catch(e) {}
        if (!hasSession) showStep(1);
      });
  }

  // ── Render Summary Sidebar ──────────────────────────────────
  function renderSummary(cart) {
    var itemsEl = summary.querySelector('.mh-co-summary-items');
    var shimmer = summary.querySelector('.mh-co-summary-shimmer');
    if (shimmer) shimmer.style.display = 'none';
    if (!itemsEl) return;

    // Clear existing
    itemsEl.textContent = '';

    // Is this a PayLater order? (quick-buy paylater or intent flagged) — so the
    // summary can show the shopper they're buying on PayLater.
    var isPaylaterOrder = (cart.attributes && cart.attributes.checkout_intent === 'paylater')
      || new URLSearchParams(window.location.search).get('intent') === 'paylater';

    cart.items.forEach(function(item) {
      var img = item.image ? item.image.replace(/(\.[^.]+)$/, '_small$1') : '';
      var variant = item.variant_title || '';

      // Build item DOM
      var si = document.createElement('div');
      si.className = 'mh-co-si';

      var imgWrap = document.createElement('div');
      imgWrap.className = 'mh-co-si-img';
      if (img) {
        var imgEl = document.createElement('img');
        imgEl.src = img;
        imgEl.alt = '';
        imgEl.loading = 'lazy';
        imgWrap.appendChild(imgEl);
      }
      var qtyBadge = document.createElement('span');
      qtyBadge.className = 'mh-co-si-qty';
      qtyBadge.textContent = item.quantity;
      imgWrap.appendChild(qtyBadge);
      si.appendChild(imgWrap);

      var info = document.createElement('div');
      info.className = 'mh-co-si-info';
      var title = document.createElement('div');
      title.className = 'mh-co-si-title';
      title.textContent = item.product_title;
      info.appendChild(title);
      if (variant) {
        var varEl = document.createElement('div');
        varEl.className = 'mh-co-si-variant';
        varEl.textContent = variant;
        info.appendChild(varEl);
      }
      if (isPaylaterOrder) {
        var plb = document.createElement('span');
        plb.className = 'mh-co-si-plbadge';
        plb.textContent = 'PayLater · Pay in 4';
        info.appendChild(plb);
      }
      si.appendChild(info);

      var price = document.createElement('div');
      price.className = 'mh-co-si-price';
      price.textContent = formatMoney(item.final_line_price);
      si.appendChild(price);

      itemsEl.appendChild(si);
    });

    // Calculate delivery fee
    var subtotalQAR = cart.total_price / 100;
    // Digital-only cart (gift cards, vouchers, downloads): Shopify cart line
    // items expose requires_shipping per item. When EVERY item is non-shippable
    // there is no delivery at all — waive the fee and hide the row. (The backend
    // re-enforces this at order creation.)
    var digitalOnly = !!(cart.items && cart.items.length > 0)
      && cart.items.every(function (i) { return i && i.requires_shipping === false; });
    var isFree = deliveryConfig.free_delivery_override
               || (deliveryConfig.free_minimum > 0 && subtotalQAR >= deliveryConfig.free_minimum);
    currentDeliveryFee = (digitalOnly || isFree) ? 0 : deliveryConfig.fee;
    var deliveryFeeCents = Math.round(currentDeliveryFee * 100);
    var grandTotalCents = cart.total_price + deliveryFeeCents;

    // Totals
    var totalsEl = summary.querySelector('.mh-co-totals');
    if (totalsEl) {
      totalsEl.textContent = '';

      var rows = [
        { label: T.subtotal, value: formatMoney(cart.total_price) }
      ];
      // Omit the Delivery row entirely for digital-only carts.
      if (!digitalOnly) {
        rows.push({ label: T.delivery, value: isFree ? T.free : formatMoney(deliveryFeeCents), cls: isFree ? 'free-delivery' : '' });
      }

      // Show threshold hint when not free (never for digital-only carts)
      if (!digitalOnly && !isFree && deliveryConfig.free_minimum > 0) {
        rows.push({
          label: '',
          value: T.freeOver + CURRENCY + ' ' + deliveryConfig.free_minimum.toFixed(2) + '+',
          cls: 'delivery-hint'
        });
      }

      rows.forEach(function(row) {
        var div = document.createElement('div');
        div.className = 'mh-co-total-row' + (row.cls ? ' ' + row.cls : '');
        var l = document.createElement('span');
        l.textContent = row.label;
        var v = document.createElement('span');
        v.textContent = row.value;
        if (row.cls === 'free-delivery') v.style.color = '#16a34a';
        if (row.cls === 'delivery-hint') {
          v.style.fontSize = '11px';
          v.style.color = '#94a3b8';
          v.style.fontStyle = 'italic';
        }
        div.appendChild(l);
        div.appendChild(v);
        totalsEl.appendChild(div);
      });

      var grand = document.createElement('div');
      grand.className = 'mh-co-total-row grand';
      var gl = document.createElement('span');
      gl.textContent = T.total;
      var gv = document.createElement('span');
      gv.textContent = formatMoney(grandTotalCents);
      grand.appendChild(gl);
      grand.appendChild(gv);
      totalsEl.appendChild(grand);

      // PayLater EMI breakdown: N interest-free monthly payments of total / N.
      if (isPaylaterOrder) {
        var inst = paylaterConfig.installments || 4;
        var perMonthCents = Math.round(grandTotalCents / inst);
        var emi = document.createElement('div');
        emi.className = 'mh-co-pl-emi';
        var emiRow = document.createElement('div');
        emiRow.className = 'mh-co-pl-emi-row';
        var el = document.createElement('span');
        el.className = 'mh-co-pl-emi-label';
        el.textContent = 'PayLater · ' + inst + T.plPayments;
        var ev = document.createElement('span');
        ev.className = 'mh-co-pl-emi-amt';
        ev.textContent = formatMoney(perMonthCents) + T.perMo;
        emiRow.appendChild(el);
        emiRow.appendChild(ev);
        var emiSub = document.createElement('div');
        emiSub.className = 'mh-co-pl-emi-sub';
        emiSub.textContent = T.plSub(inst, formatMoney(perMonthCents));
        emi.appendChild(emiRow);
        emi.appendChild(emiSub);
        totalsEl.appendChild(emi);
      }
    }
  }

  // ── Tap Card SDK (Inline Card Form) ────────────────────────
  var _tapSdkLoaded = false;
  var _tapSdkReady = false;
  var _tapCardToken = null;       // tok_xxx from onSuccess
  var _tapCardValid = false;      // true when card input is valid
  var _tapPublishableKey = '';

  function initTapCardSDK() {
    if (_tapSdkLoaded) return; // already loaded or loading
    _tapSdkLoaded = true;

    var statusEl = document.getElementById('mhTapCardStatus');
    if (statusEl) statusEl.textContent = T.cardLoading;

    // 1. Fetch publishable key from backend
    fetch(BACKEND_URL + 'tap_config_public.php')
    .then(function(r) { return r.json(); })
    .then(function(d) {
      if (!d.success || !d.publishable_key) {
        if (statusEl) statusEl.textContent = '';
        return;
      }
      _tapPublishableKey = d.publishable_key;

      // 2. Load Tap Card SDK script
      if (document.getElementById('tapCardScript')) {
        renderTapCardForm();
        return;
      }
      var s = document.createElement('script');
      s.id = 'tapCardScript';
      s.src = 'https://tap-sdks.b-cdn.net/card/1.0.2/index.js';
      s.async = true;
      s.onload = function() { renderTapCardForm(); };
      s.onerror = function() {
        if (statusEl) statusEl.textContent = T.cardLoadFail;
        _tapSdkLoaded = false; // allow retry
      };
      document.head.appendChild(s);
    })
    .catch(function() {
      if (statusEl) statusEl.textContent = '';
      _tapSdkLoaded = false;
    });
  }

  function renderTapCardForm() {
    if (!window.CardSDK || !_tapPublishableKey) return;
    var statusEl = document.getElementById('mhTapCardStatus');

    try {
      var emailVal = (root.querySelector('[name="co_email"]') || {}).value || '';
      var phoneNum = (root.querySelector('.mh-co-phone-num') || {}).value || '';

      window.CardSDK.renderTapCard('card-sdk-id', {
        publicKey: _tapPublishableKey,
        transaction: {
          amount: cartData ? (cartData.total_price / 100) + currentDeliveryFee : 0,
          currency: 'QAR'
        },
        customer: {
          name: [{ lang: 'en', first: emailVal.split('@')[0] || 'Customer', last: '' }],
          contact: {
            email: emailVal,
            phone: { countryCode: '974', number: phoneNum.replace(/\D/g, '') }
          }
        },
        acceptance: {
          supportedBrands: ['VISA', 'MASTERCARD', 'AMERICAN_EXPRESS'],
          supportedCards: 'ALL'
        },
        fields: { cardHolder: true },
        addons: { displayPaymentBrands: true, loader: true },
        interface: {
          locale: 'en',
          theme: 'light',
          edges: 'curved',
          direction: 'ltr'
        },
        onReady: function() {
          _tapSdkReady = true;
          if (statusEl) statusEl.textContent = '';
        },
        onFocus: function() {},
        onBinIdentification: function(data) {},
        onValidInput: function(valid) {
          _tapCardValid = !!valid;
          _tapCardToken = null; // reset token when input changes
        },
        onInvalidInput: function(invalid) {
          _tapCardValid = false;
          _tapCardToken = null;
        },
        onSuccess: function(data) {
          // data = { id: "tok_xxx", status: "ACTIVE", card: { brand, last_four, ... } }
          _tapCardToken = (data && data.id) ? data.id : null;
          if (_tapCardToken) {
            processCardTokenPayment();
          }
        },
        onError: function(err) {
          _tapCardToken = null;
          showOverlay(false);
          var placeBtn = root.querySelector('[data-action="place-order"]');
          if (placeBtn) placeBtn.disabled = false;

          // Check for bundle_id mismatch (domain not registered with Tap)
          var errStr = JSON.stringify(err || '');
          if (errStr.indexOf('bundle_id') > -1 || errStr.indexOf('1220') > -1) {
            // Domain config issue — fall back to redirect flow silently
            _tapSdkReady = false;
            var cardWrap = document.getElementById('mhTapCardWrap');
            if (cardWrap) cardWrap.style.display = 'none';
            var statusEl = document.getElementById('mhTapCardStatus');
            if (statusEl) statusEl.textContent = '';
          } else {
            showAlert(T.cardError, 'error');
          }
        }
      });
    } catch (e) {
      if (statusEl) statusEl.textContent = T.cardInitFail;
    }
  }

  // Called after tokenization succeeds — send token to backend to create charge
  function processCardTokenPayment() {
    showOverlay(true);
    var placeBtn = root.querySelector('[data-action="place-order"]');
    if (placeBtn) placeBtn.disabled = true;

    var payload = buildPayload();
    payload.token_id = _tapCardToken;

    fetch(BACKEND_URL + 'tap_create_charge.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    .then(function(r) { return r.json(); })
    .then(function(data) {
      if (!data.success) {
        showOverlay(false);
        if (placeBtn) placeBtn.disabled = false;
        showAlert(data.message || T.payFailed, 'error');
        return;
      }

      var status = (data.status || '').toLowerCase();

      if (status === 'captured') {
        // Payment captured immediately (no 3DS needed) — show thank you
        handlePaymentSuccess(data);
      } else if (data.redirect_url) {
        // 3DS required — redirect to 3DS page (will come back via tap_payment_result)
        window.location.href = data.redirect_url;
      } else {
        // Pending — poll or redirect to result page
        window.location.href = 'https://menahub.com/pages/tap-payment-result?ref=' +
          encodeURIComponent(data.order_ref) + '&tap_id=' + encodeURIComponent(data.charge_id);
      }
    })
    .catch(function() {
      showOverlay(false);
      if (placeBtn) placeBtn.disabled = false;
      showAlert(T.netError, 'error');
    });
  }

  // Handle immediate capture success (no redirect needed)
  function handlePaymentSuccess(data) {
    // Clear Shopify cart
    fetch('/cart/clear.js', { method: 'POST' }).catch(function() {});

    // Show thank you screen
    showOverlay(false);
    root.querySelector('.mh-co-body').style.display = 'none';
    root.querySelector('.mh-co-stepper').style.display = 'none';

    if (thanks) {
      thanks.classList.add('visible');
      var orderNum = thanks.querySelector('.order-num');
      var orderTotal = thanks.querySelector('.order-total');
      if (orderNum) orderNum.textContent = 'Order: ' + (data.order_ref || '');
      if (orderTotal) orderTotal.textContent = (data.currency || 'QAR') + ' ' + (data.amount || '');
    }
  }

  // ── Step Navigation ─────────────────────────────────────────
  function showStep(n) {
    panels.forEach(function(p, i) {
      p.classList.toggle('visible', i === n - 1);
    });
    steps.forEach(function(s, i) {
      s.classList.remove('active', 'completed');
      if (i < n - 1) s.classList.add('completed');
      if (i === n - 1) s.classList.add('active');
    });
    stepLines.forEach(function(l, i) {
      l.classList.toggle('done', i < n - 1);
    });
    currentStep = n;
    hideAlert();

    root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function goToStep(n) {
    if (n > currentStep) {
      var errors = [];
      if (currentStep === 1) errors = validateStep1();
      if (currentStep === 2) errors = validateStep2();
      if (errors.length > 0) {
        showAlert(errors.join('. '), 'error');
        return;
      }
      // Tracking: fire add_shipping_info as the user moves from
      // step 2 (address) into step 3 (payment) — at this moment
      // the shipping address has been validated and accepted.
      if (currentStep === 2 && n === 3) {
        trackAddShippingInfo(cartData);
      }
    }
    showStep(n);
  }

  // ── Validation ──────────────────────────────────────────────
  function validateStep1() {
    // Already verified via live session — skip all Step 1 checks
    if (sessionPrefilled) return [];

    var errors = [];
    var emailInput = root.querySelector('[name="co_email"]');
    var phoneCodeInput = root.querySelector('[name="co_phone_code"]');
    var phoneNumInput = root.querySelector('[name="co_phone_number"]');

    // Phone must be verified via OTP
    if (!phoneVerified) {
      errors.push(T.verifyPhoneFirst);
    }

    var email = (emailInput.value || '').trim().toLowerCase();
    var emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(email)) {
      errors.push(T.emailRequired);
      setFieldError(emailInput);
    } else {
      var domain = email.split('@')[1];
      if (DISPOSABLE_DOMAINS.indexOf(domain) !== -1) {
        errors.push(T.disposableEmail);
        setFieldError(emailInput);
      }
    }

    var code = phoneCodeInput.value;
    var num = (phoneNumInput.value || '').replace(/[^0-9]/g, '');
    var rule = GCC_PHONE_RULES[code];
    if (!rule) {
      errors.push(T.countryCode);
      setFieldError(phoneCodeInput);
    } else if (num.length !== rule.digits) {
      errors.push('Phone must be ' + rule.digits + ' digits for ' + rule.country);
      setFieldError(phoneNumInput);
    }

    return errors;
  }

  function validateStep2() {
    var errors = [];
    var cityInput = root.querySelector('[name="co_city"]');
    var zoneInput = root.querySelector('[name="co_area"]');
    var streetInput = root.querySelector('[name="co_street"]');
    var buildingInput = root.querySelector('[name="co_building"]');

    if (!(cityInput.value || '').trim()) {
      errors.push('City is required');
      setFieldError(cityInput);
    }
    if (!(zoneInput.value || '').trim()) {
      errors.push('Zone number is required');
      setFieldError(zoneInput);
    }
    if (!(streetInput.value || '').trim()) {
      errors.push('Street number is required');
      setFieldError(streetInput);
    }
    if (!(buildingInput.value || '').trim()) {
      errors.push('Building number is required');
      setFieldError(buildingInput);
    }

    return errors;
  }

  function setFieldError(el) {
    el.classList.add('error');
    var field = el.closest('.mh-co-field');
    if (field) {
      var errEl = field.querySelector('.mh-co-error');
      if (errEl) errEl.classList.add('show');
    }
  }

  // ── OTP UI ───────────────────────────────────────────────────
  // Adapt popup heading/body text to the active channel. Called once
  // after checkout_config.php returns, and again before the popup opens.
  function applyOtpChannelUi() {
    var heading = document.querySelector('#otp-popup h3');
    var sendCopy = document.querySelector('#otp-popup-send p');
    var verifyCopy = document.querySelector('#otp-popup-verify p');
    var sendBtn = document.querySelector('[data-action="send-otp"]');
    if (otpChannel === 'email') {
      if (heading) heading.textContent = T.otpEmailTitle;
      if (sendCopy) sendCopy.textContent = "We'll send a 6-digit verification code to your email.";
      if (verifyCopy) verifyCopy.textContent = T.otpEmailEnter;
      if (sendBtn) sendBtn.textContent = T.otpEmailBtn;
    } else {
      if (heading) heading.textContent = T.otpPhoneTitle;
      if (sendCopy) sendCopy.textContent = "We'll send a 6-digit verification code to your phone number.";
      if (verifyCopy) verifyCopy.textContent = T.otpPhoneEnter;
      if (sendBtn) sendBtn.textContent = T.otpPhoneBtn;
    }
  }

  // ── OTP Popup ────────────────────────────────────────────────
  function openOtpPopup() {
    // Validate email always — used for receipts even when channel is SMS
    var email = (root.querySelector('[name="co_email"]').value || '').trim().toLowerCase();
    var emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(email)) {
      showAlert('Please enter a valid email address first', 'error');
      return;
    }
    var domain = email.split('@')[1];
    if (DISPOSABLE_DOMAINS.indexOf(domain) !== -1) {
      showAlert(T.disposableEmail, 'error');
      return;
    }

    // Phone is still required as delivery contact, regardless of OTP channel.
    var phoneNum = root.querySelector('[name="co_phone_number"]').value.replace(/[^0-9]/g, '');
    if (phoneNum.length !== 8) {
      showAlert('Please enter a valid 8-digit Qatar phone number', 'error');
      return;
    }

    hideAlert();

    // Apply channel-specific labels
    applyOtpChannelUi();

    // Show popup with masked target
    var popup = document.getElementById('otp-popup');
    var targetDisplay = document.getElementById('otp-popup-phone');
    if (targetDisplay) {
      if (otpChannel === 'email') {
        var atIdx = email.indexOf('@');
        var local = atIdx > 0 ? email.slice(0, atIdx) : email;
        var dom = atIdx > 0 ? email.slice(atIdx) : '';
        var maskedLocal = local.length <= 2 ? local : local.slice(0, 2) + '****';
        targetDisplay.textContent = maskedLocal + dom;
      } else {
        targetDisplay.textContent = '+974 ' + phoneNum;
      }
    }

    // Reset popup state
    document.getElementById('otp-popup-send').style.display = 'block';
    document.getElementById('otp-popup-verify').style.display = 'none';
    var alertEl = document.getElementById('otp-popup-alert');
    if (alertEl) alertEl.textContent = '';

    popup.classList.add('show');
  }

  function closeOtpPopup() {
    var popup = document.getElementById('otp-popup');
    popup.classList.remove('show');
  }

  // ── Complete-your-details popup ──────────────────────────────
  // Shown when a logged-in account is missing a phone and/or email. Collects
  // ONLY the missing field(s), then hands off to the existing OTP popup which
  // verifies and (on success) persists the value to the account before the
  // flow advances to Step 2.
  var collectNeed = { phone: false, email: false };
  function openProfileCollect(need) {
    collectNeed = { phone: !!(need && need.phone), email: !!(need && need.email) };
    var popup = document.getElementById('profile-collect-popup');
    if (!popup) { goToStep(1); return; }
    var emailField = popup.querySelector('.mh-collect-email');
    var phoneField = popup.querySelector('.mh-collect-phone');
    var emailIn = document.getElementById('collect_email');
    var phoneIn = document.getElementById('collect_phone');
    if (emailField) emailField.style.display = collectNeed.email ? 'block' : 'none';
    if (phoneField) phoneField.style.display = collectNeed.phone ? 'block' : 'none';
    // Carry over an email the account already has (phone-only-missing case).
    if (emailIn && !collectNeed.email) {
      emailIn.value = ((root.querySelector('[name="co_email"]') || {}).value || '');
    }
    var alertEl = document.getElementById('collect-popup-alert');
    if (alertEl) alertEl.textContent = '';
    popup.classList.add('show');
    setTimeout(function() {
      try { (collectNeed.phone ? phoneIn : emailIn).focus(); } catch(e) {}
    }, 50);
  }

  function closeCollect() {
    var popup = document.getElementById('profile-collect-popup');
    if (popup) popup.classList.remove('show');
  }

  function collectContinue() {
    var alertEl = document.getElementById('collect-popup-alert');
    function warn(m) { if (alertEl) alertEl.textContent = m; }
    warn('');

    var emailIn = document.getElementById('collect_email');
    var phoneIn = document.getElementById('collect_phone');
    var coEmail = root.querySelector('[name="co_email"]');
    var coPhone = root.querySelector('[name="co_phone_number"]');

    var email = ((collectNeed.email ? (emailIn && emailIn.value) : (coEmail && coEmail.value)) || '')
      .trim().toLowerCase();
    var emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(email)) { warn(T.emailRequired); return; }
    if (DISPOSABLE_DOMAINS.indexOf(email.split('@')[1]) !== -1) { warn(T.disposableEmail); return; }

    var phone = ((collectNeed.phone ? (phoneIn && phoneIn.value) : (coPhone && coPhone.value)) || '')
      .replace(/\D/g, '');
    if (phone.length !== 8) { warn('Please enter a valid 8-digit Qatar phone number'); return; }

    // Write into the canonical Step-1 inputs the OTP + order flow reads from.
    if (coEmail) coEmail.value = email;
    if (coPhone) coPhone.value = phone;

    // Hand off to the existing OTP popup: it sends the code, verifies, then
    // persistProfileContact() saves the detail and the flow advances to Step 2.
    closeCollect();
    openOtpPopup();
  }

  // ── OTP Send ─────────────────────────────────────────────────
  function sendOtp() {
    var phoneCode = root.querySelector('[name="co_phone_code"]').value;
    var phoneNum = root.querySelector('[name="co_phone_number"]').value.replace(/[^0-9]/g, '');
    var email = (root.querySelector('[name="co_email"]').value || '').trim().toLowerCase();
    var sendBtn = root.querySelector('[data-action="do-send-otp"]');

    if (sendBtn) sendBtn.disabled = true;
    if (sendBtn) sendBtn.textContent = T.sending;

    var payload = { action: 'send', channel: otpChannel };
    if (otpChannel === 'email') {
      payload.email = email;
    } else {
      payload.phone_code = phoneCode;
      payload.phone_number = phoneNum;
    }

    fetch(BACKEND_URL + 'web_checkout_otp.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    .then(function(r) { return r.json(); })
    .then(function(data) {
      if (data.success) {
        // Show verify section in popup
        document.getElementById('otp-popup-send').style.display = 'none';
        document.getElementById('otp-popup-verify').style.display = 'block';

        var expiresIn = data.expires_in || 600;
        startOtpTimer(expiresIn);
      } else {
        var alertEl = document.getElementById('otp-popup-alert');
        if (alertEl) alertEl.textContent = data.message || T.otpFail;
        if (sendBtn) sendBtn.disabled = false;
        if (sendBtn) sendBtn.textContent = T.sendOtp;
      }
    })
    .catch(function() {
      var alertEl = document.getElementById('otp-popup-alert');
      if (alertEl) alertEl.textContent = T.netErrorShort;
      if (sendBtn) sendBtn.disabled = false;
      if (sendBtn) sendBtn.textContent = T.sendOtp;
    });
  }

  function startOtpTimer(seconds) {
    otpExpiresAt = Date.now() + (seconds * 1000);
    var timerEl = document.getElementById('otp-timer');
    var resendBtn = document.getElementById('otp-resend-btn');
    if (resendBtn) resendBtn.style.display = 'none';

    if (otpTimerInterval) clearInterval(otpTimerInterval);

    otpTimerInterval = setInterval(function() {
      var remaining = Math.max(0, Math.floor((otpExpiresAt - Date.now()) / 1000));
      var mins = Math.floor(remaining / 60);
      var secs = remaining % 60;

      if (timerEl) timerEl.textContent = T.codeExpires + mins + ':' + (secs < 10 ? '0' : '') + secs;

      if (remaining <= 0) {
        clearInterval(otpTimerInterval);
        if (timerEl) timerEl.textContent = T.codeExpired;
        if (resendBtn) resendBtn.style.display = 'inline';
      }
    }, 1000);
  }

  // ── OTP Verify ──────────────────────────────────────────────
  function verifyOtp() {
    var phoneCode = root.querySelector('[name="co_phone_code"]').value;
    var phoneNum = root.querySelector('[name="co_phone_number"]').value.replace(/[^0-9]/g, '');
    var email = (root.querySelector('[name="co_email"]').value || '').trim().toLowerCase();
    var otpInput = (root.querySelector('[name="co_otp"]').value || '').replace(/[^0-9]/g, '');
    var verifyBtn = root.querySelector('[data-action="verify-otp"]');

    if (otpInput.length !== 6) {
      showAlert('Please enter a valid 6-digit OTP code', 'error');
      return;
    }

    if (verifyBtn) verifyBtn.disabled = true;
    if (verifyBtn) verifyBtn.textContent = T.verifying;

    var payload = { action: 'verify', channel: otpChannel, otp: otpInput };
    if (otpChannel === 'email') {
      payload.email = email;
    } else {
      payload.phone_code = phoneCode;
      payload.phone_number = phoneNum;
    }

    fetch(BACKEND_URL + 'web_checkout_otp.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    .then(function(r) { return r.json(); })
    .then(function(data) {
      if (data.success) {
        phoneVerified = true;
        otpToken = data.otp_token || '';
        otpTs = data.otp_ts || 0;
        otpIdentifier = data.otp_identifier || (otpChannel === 'email' ? email : (phoneCode + phoneNum));
        if (otpTimerInterval) clearInterval(otpTimerInterval);

        // Save the just-verified phone/email onto the logged-in account, so an
        // account that was missing one now has it stored and won't be blocked
        // at checkout next time. Non-fatal — the order proceeds regardless.
        persistProfileContact(phoneCode, phoneNum, email);

        // Close popup
        closeOtpPopup();

        // Show verified badge in Step 1
        var verifiedSection = document.getElementById('otp-verified-section');
        if (verifiedSection) {
          var label = verifiedSection.querySelector('span');
          if (label) label.textContent = otpChannel === 'email' ? 'Email verified' : 'Phone number verified';
          verifiedSection.style.display = 'block';
        }

        // Hide "Verify & Continue" button, show "Continue to Shipping"
        var sendOtpBtn = root.querySelector('[data-action="send-otp"]');
        var nextBtn = root.querySelector('[data-action="next-1"]');
        if (sendOtpBtn) sendOtpBtn.style.display = 'none';
        if (nextBtn) nextBtn.style.display = '';

        // Auto-advance to Step 2
        setTimeout(function() { goToStep(2); }, 500);
      } else {
        var alertEl = document.getElementById('otp-popup-alert');
        if (alertEl) alertEl.textContent = data.message || T.otpInvalid;
        if (verifyBtn) verifyBtn.disabled = false;
        if (verifyBtn) verifyBtn.textContent = 'Verify';
      }
    })
    .catch(function() {
      var alertEl = document.getElementById('otp-popup-alert');
      if (alertEl) alertEl.textContent = T.netErrorShort;
      if (verifyBtn) verifyBtn.disabled = false;
      if (verifyBtn) verifyBtn.textContent = 'Verify';
    });
  }

  // ── PayLater Visibility + PAY-LATER-ONLY enforcement ────────
  // Rule (chosen by merchant): if the cart contains PayLater products it must
  // contain ONLY PayLater products — then the sole payment option is PayLater.
  //  - all items eligible + in range  -> hide COD/Card, show ONLY PayLater
  //  - mixed (some not eligible)       -> BLOCK checkout with a message
  //  - all eligible but out of range   -> fall back to COD/Card with a note
  //  - no PayLater items               -> normal (COD/Card)
  var mhCartBlockedMixed = false;

  function mhPlNote(msg, kind) {
    var pm = document.querySelector('.mh-co-pm');
    if (!pm) return;
    var n = document.getElementById('mh-co-pl-note');
    if (!n) {
      n = document.createElement('div');
      n.id = 'mh-co-pl-note';
      n.style.cssText = 'margin-bottom:12px;padding:10px 12px;border-radius:8px;font-size:13px;font-weight:600;line-height:1.4';
      pm.insertBefore(n, pm.firstChild);
    }
    if (!msg) { n.style.display = 'none'; return; }
    n.textContent = msg;
    n.style.display = '';
    if (kind === 'block') { n.style.background = '#fef2f2'; n.style.border = '1px solid #fecaca'; n.style.color = '#b91c1c'; }
    else { n.style.background = '#eff6ff'; n.style.border = '1px solid #bfdbfe'; n.style.color = '#1d4ed8'; }
  }

  function updatePaylaterVisibility(cart) {
    var plEl = document.getElementById('mh-co-pm-paylater');
    var emiEl = document.getElementById('mh-co-bnpl-emi');
    var codEl = document.querySelector('input[name="co_payment"][value="cod"]');
    var cardEl = document.querySelector('input[name="co_payment"][value="card"]');
    codEl = codEl ? (codEl.closest('.mh-co-pm-option') || codEl.parentElement) : null;
    cardEl = cardEl ? (cardEl.closest('.mh-co-pm-option') || cardEl.parentElement) : null;
    if (!plEl) return;

    mhCartBlockedMixed = false;

    function showCodCard(show) {
      if (codEl) codEl.style.display = show ? '' : 'none';
      if (cardEl) cardEl.style.display = show ? '' : 'none';
    }
    function selectPm(val) {
      var r = document.querySelector('input[name="co_payment"][value="' + val + '"]');
      if (!r) return;
      r.checked = true;
      document.querySelectorAll('.mh-co-pm-option').forEach(function (o) { o.classList.remove('selected'); });
      var opt = r.closest('.mh-co-pm-option');
      if (opt) opt.classList.add('selected');
      var cardWrap = document.getElementById('mhTapCardWrap');
      if (cardWrap) cardWrap.style.display = (val === 'card') ? '' : 'none';
    }

    // Per-item eligibility
    var eligibleCount = 0, ineligibleCount = 0, bnplTotal = 0;
    if (cart && cart.items) {
      cart.items.forEach(function (it) {
        var price = (it.final_price || it.price) * it.quantity / 100;
        if (bnplEligibleProductIds.indexOf(it.product_id) !== -1) { eligibleCount++; bnplTotal += price; }
        else { ineligibleCount++; }
      });
    }
    var hasPaylater = paylaterConfig.enabled && eligibleCount > 0;
    var allEligible = hasPaylater && ineligibleCount === 0;

    if (!hasPaylater) {
      // Normal cart — COD/Card, no PayLater.
      plEl.style.display = 'none';
      showCodCard(true);
      mhPlNote('', '');
      return;
    }

    if (!allEligible) {
      // Mixed cart — block. Only PayLater products may be bought together.
      plEl.style.display = 'none';
      showCodCard(false);
      mhCartBlockedMixed = true;
      mhPlNote('Your cart mixes PayLater products with regular products. PayLater orders must contain ONLY PayLater-enabled products — please remove the others, or check them out in a separate order.', 'block');
      return;
    }

    // All items eligible
    if (bnplTotal >= paylaterConfig.min_order && bnplTotal <= paylaterConfig.max_order) {
      // PAY-LATER ONLY
      showCodCard(false);
      plEl.style.display = '';
      selectPm('paylater');
      if (emiEl) {
        var inst = (bnplTotal / paylaterConfig.installments).toFixed(2);
        emiEl.textContent = paylaterConfig.installments + ' × ' + CURRENCY + ' ' + inst + '/mo · 0% interest';
        emiEl.style.display = '';
      }
      mhPlNote('This is a PayLater order — pay in ' + paylaterConfig.installments + ' interest-free monthly installments.', 'info');
    } else {
      // Eligible but outside PayLater's range — fall back to COD/Card.
      plEl.style.display = 'none';
      showCodCard(true);
      var plr = plEl.querySelector('input[type="radio"]');
      if (plr && plr.checked) selectPm('cod');
      mhPlNote('PayLater applies to orders between ' + CURRENCY + ' ' + paylaterConfig.min_order + ' and ' + CURRENCY + ' ' + paylaterConfig.max_order + '. Please pay by Card or COD for this amount.', 'info');
    }
  }

  // ── Place Order ─────────────────────────────────────────────
  function placeOrder() {
    // PayLater-only enforcement: a cart mixing PayLater + regular products
    // cannot be checked out together.
    if (mhCartBlockedMixed) {
      showAlert('Please choose only PayLater-enabled products. Remove the non-PayLater items (or order them separately) to continue.', 'error');
      return;
    }

    var e1 = validateStep1();
    var e2 = validateStep2();
    if (e1.length > 0 || e2.length > 0) {
      showAlert(e1.concat(e2).join('. '), 'error');
      return;
    }

    var pmRadio = root.querySelector('input[name="co_payment"]:checked');
    if (!pmRadio) {
      showAlert('Please select a payment method', 'error');
      return;
    }

    var paymentMethod = pmRadio.value;

    // Save address (if none saved) + refresh contact on the account, so the
    // next order prefills and nothing needs retyping. Fire-and-forget.
    persistCheckoutDetails();

    // Tracking: fire add_payment_info / AddPaymentInfo the moment
    // the user clicks Place Order with a chosen payment method —
    // this is the highest-intent moment before order submission.
    trackAddPaymentInfo(cartData, paymentMethod);

    // Card — use inline Tap Card SDK if loaded, otherwise fall back to redirect flow
    if (paymentMethod === 'card') {
      if (_tapSdkReady && window.CardSDK) {
        // ── Inline SDK flow: tokenize card, then create charge with token ──
        if (!_tapCardValid) {
          showAlert('Please fill in your card details.', 'error');
          return;
        }
        showOverlay(true);
        var placeBtn = root.querySelector('[data-action="place-order"]');
        if (placeBtn) placeBtn.disabled = true;
        // Trigger tokenization — onSuccess callback will call processCardTokenPayment()
        window.CardSDK.tokenize();
      } else {
        // ── Fallback: redirect flow (SDK not loaded or Tap key not available) ──
        showOverlay(true);
        var placeBtn = root.querySelector('[data-action="place-order"]');
        if (placeBtn) placeBtn.disabled = true;
        var cardPayload = buildPayload();
        fetch(BACKEND_URL + 'tap_create_charge.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(cardPayload)
        })
        .then(function(r) { return r.json(); })
        .then(function(data) {
          if (data.success && data.redirect_url) {
            window.location.href = data.redirect_url;
          } else if (data.message && data.message.indexOf('unavailable') !== -1) {
            redirectToShopifyCheckout();
          } else {
            showOverlay(false);
            if (placeBtn) placeBtn.disabled = false;
            showAlert(data.message || 'Payment initialization failed. Please try again.', 'error');
          }
        })
        .catch(function() {
          redirectToShopifyCheckout();
        });
      }
      return;
    }

    // PayLater BNPL — split cart if mixed, then redirect to PayLater page
    if (paymentMethod === 'paylater') {
      var placeBtn2 = root.querySelector('[data-action="place-order"]');

      // Determine which items are eligible
      var eligibleItems = [];
      var ineligibleItems = [];
      if (cartData && cartData.items) {
        cartData.items.forEach(function(item) {
          if (bnplEligibleProductIds.indexOf(item.product_id) !== -1) {
            eligibleItems.push(item);
          } else {
            ineligibleItems.push(item);
          }
        });
      }

      // Mixed cart — show split confirmation modal
      if (ineligibleItems.length > 0 && eligibleItems.length > 0) {
        showBnplSplitModal(eligibleItems, ineligibleItems, function() {
          // User confirmed — proceed with BNPL for eligible items only
          proceedBnplAfterSplit(eligibleItems, ineligibleItems, placeBtn2);
        });
        return;
      }

      // All items eligible — normal BNPL flow (no split needed)
      showOverlay(true);
      if (placeBtn2) placeBtn2.disabled = true;

      var bnplPayload = buildPayload();
      bnplPayload.payment_method = 'paylater';

      fetch(BACKEND_URL + 'bnpl_create_charge.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bnplPayload)
      })
      .then(function(r) { return r.json(); })
      .then(function(data) {
        if (data.success && data.redirect_url) {
          window.location.href = data.redirect_url;
        } else {
          showOverlay(false);
          if (placeBtn2) placeBtn2.disabled = false;
          showAlert(data.message || 'BNPL payment initialization failed. Please try again.', 'error');
        }
      })
      .catch(function() {
        showOverlay(false);
        if (placeBtn2) placeBtn2.disabled = false;
        showAlert(T.netError, 'error');
      });
      return;
    }

    // COD flow
    showOverlay(true);
    var placeBtn = root.querySelector('[data-action="place-order"]');
    if (placeBtn) placeBtn.disabled = true;

    // Use shared buildPayload() which handles email resolution + mh_token
    var payload = buildPayload();
    payload.payment_method = 'cod';

    fetch(BACKEND_URL + 'web_create_order.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    .then(function(r) { return r.json(); })
    .then(function(data) {
      showOverlay(false);
      if (data.success) {
        // Tracking: fire purchase / Purchase as the very first
        // action on success — before clearCart() empties cartData.
        trackPurchase(
          data.order_number,
          data.total,
          data.currency,
          cartData,
          paymentMethod
        );
        clearCart();
        showThankYou(data.order_number, data.total, data.currency);
      } else {
        if (placeBtn) placeBtn.disabled = false;
        var msg = data.message || 'Order failed. Please try again.';
        if (data.errors && data.errors.length) {
          msg += ' ' + data.errors.join('. ');
        }
        showAlert(msg, 'error');
      }
    })
    .catch(function() {
      showOverlay(false);
      if (placeBtn) placeBtn.disabled = false;
      showAlert(T.netError, 'error');
    });
  }

  // ── BNPL Split Modal ─────────────────────────────────────────

  function showBnplSplitModal(eligibleItems, ineligibleItems, onConfirm) {
    var overlay = document.getElementById('mhBnplSplitOverlay');
    var body = document.getElementById('mhBnplSplitBody');
    var cancelBtn = document.getElementById('mhBnplSplitCancel');
    var confirmBtn = document.getElementById('mhBnplSplitConfirm');
    if (!overlay || !body) return;

    var eligibleTotal = eligibleItems.reduce(function(sum, it) {
      return sum + (it.final_price || it.price) * it.quantity / 100;
    }, 0);
    var installment = (eligibleTotal / paylaterConfig.installments).toFixed(2);

    // Build eligible items list
    var eligibleHtml = eligibleItems.map(function(it) {
      var price = ((it.final_price || it.price) * it.quantity / 100).toFixed(2);
      return '<div class="mh-bnpl-split-item">' +
        '<div class="mh-bnpl-split-item-dot" style="background:#16a34a;"></div>' +
        '<span style="flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">' + it.product_title + '</span>' +
        '<strong>' + CURRENCY + ' ' + price + '</strong>' +
      '</div>';
    }).join('');

    // Build ineligible items list
    var ineligibleNames = ineligibleItems.map(function(it) { return it.product_title; }).join(', ');

    body.innerHTML =
      '<div class="mh-bnpl-split-eligible">' +
        '<div class="mh-bnpl-split-eligible-label">Qualifying for PayLater</div>' +
        eligibleHtml +
      '</div>' +
      '<div class="mh-bnpl-split-emi">' +
        paylaterConfig.installments + ' &times; ' + CURRENCY + ' ' + installment + '/mo &middot; 0% interest' +
      '</div>' +
      '<div class="mh-bnpl-split-remaining">' +
        '<strong>Remaining in cart</strong>' +
        'The following will stay in your cart for a separate purchase: ' + ineligibleNames +
      '</div>';

    overlay.classList.add('show');

    // Clean up old listeners
    var newCancel = cancelBtn.cloneNode(true);
    var newConfirm = confirmBtn.cloneNode(true);
    cancelBtn.parentNode.replaceChild(newCancel, cancelBtn);
    confirmBtn.parentNode.replaceChild(newConfirm, confirmBtn);

    newCancel.addEventListener('click', function() {
      overlay.classList.remove('show');
    });
    newConfirm.addEventListener('click', function() {
      overlay.classList.remove('show');
      onConfirm();
    });
  }

  function proceedBnplAfterSplit(eligibleItems, ineligibleItems, placeBtn2) {
    showOverlay(true);
    if (placeBtn2) placeBtn2.disabled = true;

    var bnplPayload = buildPayload();
    bnplPayload.payment_method = 'paylater';
    bnplPayload.items = eligibleItems.map(function(item) {
      return {
        variant_id: item.variant_id,
        quantity: item.quantity,
        price: item.final_price,
        title: item.product_title,
        product_id: item.product_id,
        image: item.image || ''
      };
    });
    var eligSubtotal = eligibleItems.reduce(function(sum, it) {
      return sum + (it.final_price || it.price) * it.quantity / 100;
    }, 0);
    // Digital-only BNPL split (gift cards/vouchers) → no delivery fee.
    var bnplDigitalOnly = eligibleItems.length > 0
      && eligibleItems.every(function (it) { return it && it.requires_shipping === false; });
    bnplPayload.delivery_fee = (bnplDigitalOnly || eligSubtotal >= (deliveryConfig.free_minimum || 300))
      ? 0 : (deliveryConfig.fee || 18);
    try {
      sessionStorage.setItem('bnpl_split_variants', JSON.stringify(
        eligibleItems.map(function(it) { return it.variant_id; })
      ));
    } catch(e) {}

    fetch(BACKEND_URL + 'bnpl_create_charge.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(bnplPayload)
    })
    .then(function(r) { return r.json(); })
    .then(function(data) {
      if (data.success && data.redirect_url) {
        window.location.href = data.redirect_url;
      } else {
        showOverlay(false);
        if (placeBtn2) placeBtn2.disabled = false;
        showAlert(data.message || 'BNPL payment initialization failed. Please try again.', 'error');
      }
    })
    .catch(function() {
      showOverlay(false);
      if (placeBtn2) placeBtn2.disabled = false;
      showAlert(T.netError, 'error');
    });
  }

  // ── Build Payload (shared by COD + Tap) ─────────────────────
  function buildPayload() {
    // Resolve email: form field → mh_user localStorage → empty
    var emailVal = (root.querySelector('[name="co_email"]').value || '').trim();
    if (!emailVal || emailVal.indexOf('noemail.local') !== -1 || emailVal.indexOf('_ph_') !== -1) {
      try {
        var lu = JSON.parse(localStorage.getItem('mh_user') || '{}');
        if (lu.email && lu.email.indexOf('noemail.local') === -1 && lu.email.indexOf('_ph_') === -1) {
          emailVal = lu.email;
        }
      } catch(e) {}
    }
    // Pass auth token so backend can look up the real email if still missing
    var sessionToken = '';
    try { sessionToken = localStorage.getItem('mh_token') || ''; } catch(e) {}

    // First-touch attribution captured by menahub-attribution.js on
    // the very first page-view of this session. Forwarded to the
    // backend so it can populate Shopify's landing_site / referring_site
    // / client_details / UTM note_attributes when creating the order.
    var attribution = {};
    try {
      if (window.MenahubAttribution && typeof window.MenahubAttribution.get === 'function') {
        attribution = window.MenahubAttribution.get() || {};
      }
    } catch (e) { /* leave as empty object */ }

    return {
      payment_method: 'card',
      otp_token: otpToken,
      otp_ts: otpTs,
      otp_identifier: otpIdentifier,
      delivery_fee: currentDeliveryFee,
      mh_token: sessionToken,
      attribution: attribution,
      customer: {
        name: '',
        email: emailVal,
        phone_code: root.querySelector('[name="co_phone_code"]').value,
        phone_number: root.querySelector('[name="co_phone_number"]').value.replace(/[^0-9]/g, '')
      },
      shipping: {
        country: root.querySelector('[name="co_country"]').value,
        city: (root.querySelector('[name="co_city"]').value || '').trim(),
        area: 'Zone ' + (root.querySelector('[name="co_area"]').value || '').trim(),
        street: 'Street ' + (root.querySelector('[name="co_street"]').value || '').trim() + ', Building ' + (root.querySelector('[name="co_building"]').value || '').trim(),
        postal_code: (root.querySelector('[name="co_zip"]').value || '').trim(),
        notes: (root.querySelector('[name="co_notes"]').value || '').trim()
      },
      items: cartData.items.map(function(item) {
        return {
          variant_id: item.variant_id,
          quantity: item.quantity,
          price: item.final_price,
          title: item.product_title,
          product_id: item.product_id,
          image: item.image || ''
        };
      })
    };
  }

  // ── Shopify Checkout Fallback ──────────────────────────────────
  function redirectToShopifyCheckout() {
    var email = (root.querySelector('[name="co_email"]').value || '').trim();
    var phoneCode = root.querySelector('[name="co_phone_code"]').value;
    var phoneNum = (root.querySelector('[name="co_phone_number"]').value || '').trim();
    var country = root.querySelector('[name="co_country"]').value;
    var city = (root.querySelector('[name="co_city"]').value || '').trim();
    var zone = (root.querySelector('[name="co_area"]').value || '').trim();
    var streetNum = (root.querySelector('[name="co_street"]').value || '').trim();
    var building = (root.querySelector('[name="co_building"]').value || '').trim();
    var zip = (root.querySelector('[name="co_zip"]').value || '').trim();
    var fullPhone = phoneCode + phoneNum;

    var namePart = email.split('@')[0].replace(/[._-]/g, ' ');
    var words = namePart.split(' ');
    var firstName = words[0] ? words[0].charAt(0).toUpperCase() + words[0].slice(1) : '';
    var lastName = words.length > 1 ? words.slice(1).map(function(w) { return w.charAt(0).toUpperCase() + w.slice(1); }).join(' ') : '';

    var params = new URLSearchParams();
    params.set('checkout[email]', email);
    params.set('checkout[shipping_address][first_name]', firstName);
    params.set('checkout[shipping_address][last_name]', lastName);
    params.set('checkout[shipping_address][address1]', 'Street ' + streetNum + ', Building ' + building);
    params.set('checkout[shipping_address][address2]', 'Zone ' + zone);
    params.set('checkout[shipping_address][city]', city);
    params.set('checkout[shipping_address][country]', country);
    params.set('checkout[shipping_address][zip]', zip);
    params.set('checkout[shipping_address][phone]', fullPhone);
    params.set('contact_email', email);

    window.location.href = '/checkout?' + params.toString();
  }

  // ── Clear Shopify Cart ──────────────────────────────────────
  function clearCart() {
    // Quick Buy uses a synthetic single-item cart — the shopper's real cart
    // was never touched, so don't clear it after a quick-buy order.
    if (isQuickMode) return;
    fetch('/cart/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json' } })
      .then(function() {
        // Update all cart badges to 0
        document.querySelectorAll('.bigcounter').forEach(function(el) {
          el.textContent = '0';
          el.classList.add('hidden-count');
        });
        // Also update mobile cart badge
        var mobileBadge = document.querySelector('.mobile-cart-badge');
        if (mobileBadge) mobileBadge.style.display = 'none';
        // Update header cart price count
        var cartPrice = document.querySelector('.cart-price');
        if (cartPrice) cartPrice.textContent = '(0)';
      })
      .catch(function() {});
  }

  // ── Thank You ───────────────────────────────────────────────
  function showThankYou(orderNumber, total, currency) {
    panels.forEach(function(p) { p.classList.remove('visible'); });
    var formCol = root.querySelector('.mh-co-form-col');
    if (formCol) formCol.style.display = 'none';
    if (summary) summary.style.display = 'none';

    steps.forEach(function(s) { s.classList.remove('active'); s.classList.add('completed'); });
    stepLines.forEach(function(l) { l.classList.add('done'); });

    var orderEl = thanks.querySelector('.order-num');
    if (orderEl) orderEl.textContent = 'Order ' + orderNumber;

    var totalEl = thanks.querySelector('.order-total');
    if (totalEl && total) totalEl.textContent = (currency || 'QAR') + ' ' + parseFloat(total).toFixed(2);

    thanks.classList.add('visible');
    root.scrollIntoView({ behavior: 'smooth', block: 'start' });

    // ── Google Ads conversion (AW-548819569 / 9dDoCP6h-94CEPGk2YUC) ──
    // The base gtag.js is loaded sitewide from layout/theme.liquid, so
    // window.gtag is ready by the time the user reaches the thank-you
    // screen. We dedupe by transaction_id (the order number) so a page
    // refresh from the user's browser won't double-count.
    fireGoogleAdsConversion(orderNumber, total, currency);
  }

  function fireGoogleAdsConversion(orderNumber, total, currency) {
    try {
      if (typeof window.gtag !== 'function') return;
      var key = 'mh_ga_fired_' + String(orderNumber || '');
      if (!orderNumber || sessionStorage.getItem(key) === '1') return;
      sessionStorage.setItem(key, '1');
      window.gtag('event', 'conversion', {
        send_to: 'AW-548819569/9dDoCP6h-94CEPGk2YUC',
        value: parseFloat(total) || 0,
        currency: currency || 'QAR',
        transaction_id: String(orderNumber)
      });
    } catch (e) { /* never break the thank-you screen */ }
  }

  // ── Helpers ─────────────────────────────────────────────────
  function formatMoney(cents) {
    return CURRENCY + ' ' + (cents / 100).toFixed(2);
  }

  function showOverlay(show) {
    if (overlay) overlay.classList.toggle('show', show);
  }

  function showAlert(msg, type) {
    if (!alertBox) return;
    alertBox.textContent = msg;
    alertBox.className = 'mh-co-alert ' + (type || 'error');
    alertBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function hideAlert() {
    if (alertBox) {
      alertBox.textContent = '';
      alertBox.className = 'mh-co-alert';
    }
  }

  // ── Boot ────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // bfcache safety net: when the user leaves for the PayLater/Tap page and hits
  // the browser Back button, the page is restored from bfcache WITHOUT re-running
  // init(), so a "Processing your order…" overlay left up before navigating stays
  // frozen on screen and blocks the whole page. On a persisted pageshow, tear down
  // every blocking state so the checkout is usable again. Query the DOM directly
  // (don't trust closure vars) so this works no matter how the restore happened.
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    var proc = document.querySelector('.mh-co-overlay');
    if (proc) proc.classList.remove('show');
    var split = document.getElementById('mhBnplSplitOverlay');
    if (split) split.classList.remove('show', 'open', 'active');
    var pb = document.querySelector('[data-action="place-order"]');
    if (pb) { pb.disabled = false; pb.classList.remove('loading'); }
    var alertEl = document.querySelector('.mh-co-alert');
    if (alertEl) { alertEl.textContent = ''; alertEl.className = 'mh-co-alert'; }
  });
})();
